import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Users, Crown, Disc3, Heart, ShieldCheck, 
  Loader2, Sun, Moon, Search, RefreshCw, Key, Trash2, AlertTriangle,
  DollarSign, TrendingUp, Calendar, Globe, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

export default function Admin() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  const [adminSecret, setAdminSecret] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingUser, setUpdatingUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Date range for revenue
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!adminSecret.trim()) {
      toast.error('Please enter admin secret');
      return;
    }
    
    setLoading(true);
    try {
      // Fetch dashboard data
      const dashboardRes = await adminAPI.getDashboard(adminSecret);
      setDashboard(dashboardRes.data);
      setStats(dashboardRes.data);
      
      // Fetch users
      const usersRes = await adminAPI.getUsers(adminSecret);
      setUsers(usersRes.data.users);
      
      setIsAuthenticated(true);
      toast.success('Admin access granted');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Invalid admin secret');
      } else {
        toast.error('Failed to authenticate');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        adminAPI.getDashboard(adminSecret, startDate || undefined, endDate || undefined),
        adminAPI.getUsers(adminSecret)
      ]);
      setDashboard(dashboardRes.data);
      setStats(dashboardRes.data);
      setUsers(usersRes.data.users);
      toast.success('Data refreshed');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = async () => {
    if (!startDate && !endDate) {
      toast.error('Please select at least one date');
      return;
    }
    setLoading(true);
    try {
      const res = await adminAPI.getDashboard(adminSecret, startDate || undefined, endDate || undefined);
      setDashboard(res.data);
      toast.success('Revenue filtered');
    } catch (err) {
      toast.error('Failed to filter revenue');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (userEmail, newPlan) => {
    setUpdatingUser(userEmail);
    try {
      await adminAPI.setUserPlan(adminSecret, userEmail, newPlan);
      setUsers(users.map(u => u.email === userEmail ? { ...u, plan: newPlan } : u));
      toast.success(`${userEmail} is now ${newPlan}`);
      refreshData();
    } catch (err) {
      toast.error(`Failed to update plan: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const openDeleteDialog = (user) => {
    setDeleteDialog({ open: true, user });
    setDeleteConfirmText('');
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, user: null });
    setDeleteConfirmText('');
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    
    setDeleting(true);
    try {
      await adminAPI.deleteUser(adminSecret, deleteDialog.user.id);
      setUsers(users.filter(u => u.id !== deleteDialog.user.id));
      toast.success(`User ${deleteDialog.user.email} deleted`);
      closeDeleteDialog();
      refreshData();
    } catch (err) {
      toast.error(`Failed to delete user: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 hero-gradient">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="text-center space-y-3">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Admin Panel</CardTitle>
              <CardDescription>Enter your admin secret to continue</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Admin Secret"
                  value={adminSecret}
                  onChange={(e) => setAdminSecret(e.target.value)}
                  className="h-12 bg-secondary border-border"
                  data-testid="admin-secret-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-primary text-primary-foreground"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Access Admin Panel
                  </>
                )}
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              <button 
                onClick={() => navigate('/login')}
                className="text-primary hover:underline"
              >
                ← Back to Login
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
              <Badge variant="outline" className="gap-1">
                <ShieldCheck className="w-3 h-3" />
                Admin
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={refreshData}
                disabled={loading}
                className="w-9 h-9"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="w-9 h-9"
              >
                {theme === 'dark' ? (
                  <Sun className="w-4 h-4 text-primary" />
                ) : (
                  <Moon className="w-4 h-4 text-primary" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 space-y-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            {dashboard && (
              <>
                {/* User Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-blue-500/10">
                          <Users className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{dashboard.users.total}</p>
                          <p className="text-sm text-muted-foreground">Total Users</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-gray-500/10">
                          <Users className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{dashboard.users.free}</p>
                          <p className="text-sm text-muted-foreground">Free Users</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-amber-500/10">
                          <Crown className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{dashboard.users.pro}</p>
                          <p className="text-sm text-muted-foreground">PRO Users</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-green-500/10">
                          <TrendingUp className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">${dashboard.revenue.mrr.toFixed(2)}</p>
                          <p className="text-sm text-muted-foreground">MRR</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Subscription Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-primary" />
                        Subscriptions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg">
                        <span>Monthly Active</span>
                        <Badge variant="secondary">{dashboard.subscriptions.monthly_active}</Badge>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg">
                        <span>Yearly Active</span>
                        <Badge variant="secondary">{dashboard.subscriptions.yearly_active}</Badge>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg">
                        <span>New (Last 30 days)</span>
                        <Badge className="bg-green-500/10 text-green-500">{dashboard.subscriptions.new_last_30_days}</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-500" />
                        Revenue
                      </CardTitle>
                      <CardDescription>
                        {dashboard.revenue.period.start || dashboard.revenue.period.end 
                          ? `${dashboard.revenue.period.start || 'Start'} - ${dashboard.revenue.period.end || 'Now'}`
                          : 'All time'
                        }
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg">
                        <span>Monthly Subscriptions</span>
                        <span className="font-semibold">${dashboard.revenue.monthly_total.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg">
                        <span>Yearly Subscriptions</span>
                        <span className="font-semibold">${dashboard.revenue.yearly_total.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg">
                        <span className="font-semibold">Total Revenue</span>
                        <span className="font-bold text-lg">${dashboard.revenue.total.toFixed(2)}</span>
                      </div>
                      
                      {/* Date Filter */}
                      <div className="pt-4 border-t border-border space-y-3">
                        <p className="text-sm font-medium">Filter by date</p>
                        <div className="flex gap-2">
                          <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="bg-secondary border-border"
                            placeholder="Start date"
                          />
                          <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="bg-secondary border-border"
                            placeholder="End date"
                          />
                        </div>
                        <Button onClick={handleDateFilter} disabled={loading} className="w-full">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Filter'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Users by Country */}
                {dashboard.users.by_country && Object.keys(dashboard.users.by_country).length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-primary" />
                        Users by Country
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {Object.entries(dashboard.users.by_country)
                          .sort(([,a], [,b]) => b - a)
                          .map(([country, count]) => (
                            <div key={country} className="flex justify-between items-center p-2 bg-secondary/50 rounded">
                              <span className="text-sm">{country}</span>
                              <Badge variant="outline">{count}</Badge>
                            </div>
                          ))
                        }
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      User Management
                    </CardTitle>
                    <CardDescription>
                      {filteredUsers.length} of {users.length} users
                    </CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-full md:w-64 bg-secondary border-border"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Country</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Collection</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Joined</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b border-border/50 hover:bg-secondary/30">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm">{user.country || '-'}</span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant={user.plan === 'PRO' || user.plan === 'LIFETIME_PRO' ? 'default' : 'secondary'}
                              className={
                                user.plan === 'LIFETIME_PRO' 
                                  ? 'bg-purple-500/10 text-purple-500 border-purple-500/20' 
                                  : user.plan === 'PRO' 
                                    ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' 
                                    : ''
                              }
                            >
                              {(user.plan === 'PRO' || user.plan === 'LIFETIME_PRO') && <Crown className="w-3 h-3 mr-1" />}
                              {user.plan === 'LIFETIME_PRO' ? 'LIFETIME' : user.plan}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm">
                              {user.copy_count} {user.copy_count === 1 ? 'record' : 'records'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-muted-foreground">
                              {new Date(user.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Select
                                value={user.plan}
                                onValueChange={(value) => handlePlanChange(user.email, value)}
                                disabled={updatingUser === user.email}
                              >
                                <SelectTrigger className="w-32 h-8 bg-secondary border-border">
                                  {updatingUser === user.email ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <SelectValue />
                                  )}
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="FREE">FREE</SelectItem>
                                  <SelectItem value="PRO">PRO</SelectItem>
                                  <SelectItem value="LIFETIME_PRO">LIFETIME PRO</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDeleteDialog(user)}
                                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'No users found matching your search' : 'No users yet'}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the user account and all associated data.
            </DialogDescription>
          </DialogHeader>
          
          {deleteDialog.user && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="font-medium">{deleteDialog.user.name}</p>
                <p className="text-sm text-muted-foreground">{deleteDialog.user.email}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {deleteDialog.user.copy_count} records in collection
                </p>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm">
                  Type <span className="font-mono font-bold text-destructive">DELETE</span> to confirm:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="bg-secondary border-border"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={closeDeleteDialog}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
